import React, { useCallback, useState } from "react";

import { faLeftLong } from "@fortawesome/pro-solid-svg-icons";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import RecordSource from "@mapmycustomers/shared/enum/RecordSource";
import {
  EntitiesSupportingHistory,
  EntityType,
  EntityTypeSupportingHistory,
} from "@mapmycustomers/shared/types/entity";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import EmbeddedModal from "@app/component/modal/EmbeddedModal";

import entityHistoryFieldModel from "../../util/fieldModel/entityHistoryFieldModel";
import { StandardFilterButton } from "../filtering";
import GenericView from "../view/GenericView/GenericView";

import HistoryTable from "./component/HistoryTable";
import styles from "./EntityHistoryModal.module.scss";

interface Props {
  entity: EntitiesSupportingHistory;
  entityType: EntityTypeSupportingHistory;
  onHide: () => void;
}

const EntityHistoryModal: React.FC<Props> = ({ entity, entityType, onHide }) => {
  const intl = useIntl();

  const [historyViewState, setHistoryViewState] = useState<ViewState>({
    columns: [],
    filter: {},
    range: { endRow: 0, startRow: 0 },
    sort: [],
    viewAs: undefined,
  });

  const handleChangeFilter = useCallback((payload: { viewState: Partial<ViewState> }) => {
    setHistoryViewState((historyViewState) => ({ ...historyViewState, ...payload.viewState }));
  }, []);

  return (
    <GenericView
      entityType={EntityType.HISTORY}
      fetcher={handleChangeFilter}
      fieldModel={entityHistoryFieldModel}
      viewState={historyViewState}
    >
      <EmbeddedModal onCancel={onHide} open>
        <div className={styles.bodyContainer}>
          <Row align="middle" className={styles.headerContainer} justify="space-between">
            <Col>
              <div className={styles.title}>
                {intl.formatMessage({
                  id: "@component.entityHistory.header.title",
                  defaultMessage: "Full Record History",
                  description: "Title of entity history modal",
                })}
              </div>
            </Col>
            <Col>
              <FontAwesomeIcon
                className={styles.closeIcon}
                icon={faTimes}
                onClick={onHide}
                size="lg"
              />
            </Col>
          </Row>
          <div className={styles.container}>
            <div className={styles.backLink} onClick={onHide}>
              <FontAwesomeIcon className={styles.backLinkIcon} icon={faLeftLong} />
              <div>
                {intl.formatMessage({
                  id: "@component.entityHistory.content.backLink",
                  defaultMessage: "Back",
                  description: "History modal - back link",
                })}
              </div>
            </div>
            <Row justify="space-between">
              <Col className={styles.entityName}>{entity.name}</Col>
              <Col>
                <StandardFilterButton
                  context="entityHistoryModal"
                  entityType={EntityType.HISTORY}
                  filterOptions={{
                    [FilterOption.AVAILABLE_SOURCES]: [
                      RecordSource.PUBLIC_API,
                      RecordSource.IMPORTS,
                      RecordSource.INTEGRATIONS,
                      RecordSource.MOBILE,
                      RecordSource.WEB,
                      RecordSource.WORKFLOWS,
                    ],
                    [FilterOption.ENTITY_TYPE]: entityType,
                  }}
                  hideSavedFilters
                  popoverClassName={styles.filterPopover}
                  showColumnGroups={false}
                />
              </Col>
            </Row>
            <HistoryTable entity={entity} entityType={entityType} />
          </div>
        </div>
      </EmbeddedModal>
    </GenericView>
  );
};

export default EntityHistoryModal;
